import apiService from './api.service';
import {
  AgentListItem,
  Agent,
  AgentFile,
  AgentPublicationRequest,
  ConversationListItem,
  ConversationDetail,
  CreateAgentPayload,
  CreatePublicationRequestPayload,
  Message,
  PinnedAgent,
  SendMessageResponse,
} from '@/types/agents';

// Re-export do helper SSE pro ChatPage importar de um único lugar.
export { streamSendMessage, QuotaStreamError, isQuotaStreamError } from './agents.stream';

type Paginated<T> = T[] | { results: T[] };

function unwrapList<T>(data: Paginated<T>): T[] {
  return Array.isArray(data) ? data : data.results ?? [];
}

class AgentsService {
  async listAgents(): Promise<AgentListItem[]> {
    const response = await apiService.get<Paginated<AgentListItem>>('/agents/');
    return unwrapList(response.data);
  }

  async listMyAgents(): Promise<AgentListItem[]> {
    const response = await apiService.get<Paginated<AgentListItem>>('/agents/?tab=mine');
    return unwrapList(response.data);
  }

  async listAvailable(): Promise<AgentListItem[]> {
    const response = await apiService.get<Paginated<AgentListItem>>('/agents/?tab=available');
    return unwrapList(response.data);
  }

  // --- Agentes fixados no dashboard (J05) ---

  async listPinned(): Promise<PinnedAgent[]> {
    const response = await apiService.get<Paginated<PinnedAgent>>('/dashboard/pinned-agents/');
    return unwrapList(response.data);
  }

  async pin(agentId: string): Promise<PinnedAgent> {
    const response = await apiService.post<PinnedAgent>('/dashboard/pinned-agents/', { agent_id: agentId });
    return response.data;
  }

  async unpin(agentId: string): Promise<void> {
    await apiService.delete(`/dashboard/pinned-agents/${agentId}/`);
  }

  async create(payload: CreateAgentPayload): Promise<Agent> {
    const response = await apiService.post<Agent>('/agents/', payload);
    return response.data;
  }

  async update(id: string, payload: Partial<CreateAgentPayload>): Promise<Agent> {
    const response = await apiService.patch<Agent>(`/agents/${id}/`, payload);
    return response.data;
  }

  async deleteAgent(id: string): Promise<void> {
    await apiService.delete(`/agents/${id}/`);
  }

  async listFiles(agentId: string): Promise<AgentFile[]> {
    const response = await apiService.get<Paginated<AgentFile>>(`/agents/${agentId}/files/`);
    return unwrapList(response.data);
  }

  async uploadFile(agentId: string, file: File, onProgress?: (pct: number) => void): Promise<AgentFile> {
    const response = await apiService.uploadFile<AgentFile>(
      `/agents/${agentId}/files/`,
      file,
      (event: unknown) => {
        const e = event as { loaded?: number; total?: number };
        if (onProgress && e?.total) onProgress(Math.round(((e.loaded ?? 0) * 100) / e.total));
      }
    );
    return response.data;
  }

  async deleteFile(agentId: string, fileId: string): Promise<void> {
    await apiService.delete(`/agents/${agentId}/files/${fileId}/`);
  }

  async getAgent(id: string): Promise<Agent> {
    const response = await apiService.get<Agent>(`/agents/${id}/`);
    return response.data;
  }

  async getSuggestions(agentId: string): Promise<string[]> {
    const response = await apiService.get<{ suggestions: string[] }>(`/agents/${agentId}/suggestions/`);
    return response.data.suggestions ?? [];
  }

  async getFileUrl(fileId: string): Promise<string | null> {
    const response = await apiService.get<{ url: string }>(`/agents/files/${fileId}/url/`);
    return response.data.url ?? null;
  }

  async listConversations(agentId: string): Promise<ConversationListItem[]> {
    const response = await apiService.get<ConversationListItem[]>(`/agents/${agentId}/conversations/`);
    return response.data;
  }

  async createConversation(agentId: string, title?: string): Promise<ConversationDetail> {
    const response = await apiService.post<ConversationDetail>(`/agents/${agentId}/conversations/`, {
      title: title ?? 'Nova conversa',
    });
    return response.data;
  }

  async getConversation(conversationId: string): Promise<ConversationDetail> {
    const response = await apiService.get<ConversationDetail>(`/agents/conversations/${conversationId}/`);
    return response.data;
  }

  async updateConversation(
    conversationId: string,
    payload: { title?: string; archived?: boolean }
  ): Promise<ConversationDetail> {
    const response = await apiService.patch<ConversationDetail>(
      `/agents/conversations/${conversationId}/`,
      payload
    );
    return response.data;
  }

  async archiveConversation(conversationId: string): Promise<void> {
    await apiService.delete(`/agents/conversations/${conversationId}/`);
  }

  async listMessages(conversationId: string): Promise<Message[]> {
    const response = await apiService.get<Message[]>(`/agents/conversations/${conversationId}/messages/`);
    return response.data;
  }

  async sendMessage(conversationId: string, content: string): Promise<SendMessageResponse> {
    const response = await apiService.post<SendMessageResponse>(
      `/agents/conversations/${conversationId}/messages/`,
      { content }
    );
    return response.data;
  }

  // --- P10: publication requests ---

  async requestPublication(
    agentId: string,
    payload: CreatePublicationRequestPayload,
  ): Promise<AgentPublicationRequest> {
    const response = await apiService.post<AgentPublicationRequest>(
      '/agents/publication-requests/',
      { agent: agentId, ...payload },
    );
    return response.data;
  }

  async getMyPublicationRequests(agentId: string): Promise<AgentPublicationRequest[]> {
    const response = await apiService.get<Paginated<AgentPublicationRequest>>(
      `/agents/publication-requests/?agent=${agentId}`,
    );
    return unwrapList(response.data);
  }
}

export default new AgentsService();
